import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  CheckCircle,
  ExternalLink,
  Eraser,
  Clock,
  User,
  Pencil,
  Trash2,
  X,
  Send,
  MessageSquare,
  FileEdit,
  ChevronDown,
  GripVertical,
  StickyNote,
  AlertTriangle,
  ShieldCheck,
  Sparkles,
  GraduationCap,
  Reply,
  BrainCircuit,
  Loader2,
} from "lucide-react";
import { useUpdateQAReview } from "@/hooks/useQAReviews";
import {
  useQAComments,
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
} from "@/hooks/useQAComments";
import { useContentComments } from "@/hooks/useContentComments";
import { useTriggerAIFix, useListAIFixes, useApproveAIFix, useRejectAIFix } from "@/hooks/useAIFixes";
import { useSummarizeComments, useTeachComment } from "@/hooks/useCommentActions";
import type { QAReviewWithContent, QARuleKey, QAComment } from "@/types/qaReview";
import { QA_RULES, getIssueCount } from "@/types/qaReview";
import { COMMENT_TYPE_CONFIG } from "@/types/contentFeedback";
import type { CommentWithReplies, ContentComment, AIFix } from "@/types/contentFeedback";

/**
 * Recursively extract plain text from a TipTap JSON node.
 * Handles nested content arrays (paragraph → text, heading → text, etc.)
 */
function extractTextFromTipTap(node: unknown): string {
  if (!node || typeof node !== "object") return "";
  const n = node as Record<string, unknown>;

  // Leaf text node
  if (typeof n.text === "string") return n.text;

  // Node with content array — recurse
  if (Array.isArray(n.content)) {
    return (n.content as unknown[]).map(extractTextFromTipTap).join("");
  }

  // Array of nodes (top-level)
  if (Array.isArray(node)) {
    return (node as unknown[]).map(extractTextFromTipTap).join("\n");
  }

  return "";
}

const MIN_WIDTH = 380;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 480;
const STORAGE_KEY = "qa-drawer-width";

interface QANotesDrawerProps {
  review: QAReviewWithContent | null;
  open: boolean;
  onClose: () => void;
  tenantId: string;
}

function getStoredWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const w = parseInt(stored, 10);
      if (w >= MIN_WIDTH && w <= MAX_WIDTH) return w;
    }
  } catch {
    // ignore
  }
  return DEFAULT_WIDTH;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------
function SectionHeader({
  icon: Icon,
  label,
  count,
  open,
  onToggle,
  variant = "default",
  rightSlot,
}: {
  icon: React.ElementType;
  label: string;
  count?: number;
  open: boolean;
  onToggle: () => void;
  variant?: "default" | "teal" | "coral";
  rightSlot?: React.ReactNode;
}) {
  const colorMap = {
    default: "text-foreground",
    teal: "text-teal-dark",
    coral: "text-coral-dark",
  };
  return (
    <div className="flex items-center justify-between w-full py-1">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 group flex-1"
      >
        <Icon className={`h-4 w-4 ${colorMap[variant]}`} />
        <span className={`text-sm font-semibold ${colorMap[variant]}`}>
          {label}
        </span>
        {count !== undefined && (
          <Badge
            variant="secondary"
            className={`text-[10px] px-1.5 h-5 ${
              variant === "teal"
                ? "bg-teal-light text-teal-dark"
                : variant === "coral"
                  ? "bg-coral-light text-coral-dark"
                  : ""
            }`}
          >
            {count}
          </Badge>
        )}
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
            open ? "rotate-0" : "-rotate-90"
          }`}
        />
      </button>
      {rightSlot}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI Fix Preview (inline in drawer)
// ---------------------------------------------------------------------------
/**
 * Re-wrap edited plain text back into TipTap paragraph node structure.
 * Used when user edits the AI suggestion before approving.
 */
function wrapTextAsTipTap(text: string, original: unknown): Record<string, unknown> {
  // If original was a TipTap node with a type, preserve its structure
  const orig = original as Record<string, unknown> | null;
  const nodeType = orig?.type ?? "paragraph";
  const attrs = orig?.attrs;

  const paragraphs = text.split("\n\n").filter(Boolean);
  if (paragraphs.length <= 1) {
    return {
      type: nodeType,
      ...(attrs ? { attrs } : {}),
      content: [{ type: "text", text: text.trim() || " " }],
    };
  }
  // Multi-paragraph: wrap in a doc-like structure matching original
  return {
    type: nodeType,
    ...(attrs ? { attrs } : {}),
    content: [{ type: "text", text: text.trim() || " " }],
  };
}

function AIFixPreviewInline({
  fix,
  contentId,
}: {
  fix: AIFix;
  contentId: string;
}) {
  const approveMutation = useApproveAIFix(contentId);
  const rejectMutation = useRejectAIFix(contentId);

  const originalText = fix.proposed_content
    ? typeof fix.proposed_content === "string"
      ? fix.proposed_content
      : extractTextFromTipTap(fix.proposed_content)
    : "";

  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(originalText);
  const isModified = editedText !== originalText;

  const isPending = fix.status === "pending";
  const isProcessing = fix.status === "processing";

  const handleApprove = () => {
    if (isModified && typeof fix.proposed_content !== "string") {
      // User edited — wrap text back into TipTap node and send as edited_content
      const edited = wrapTextAsTipTap(editedText, fix.proposed_content);
      approveMutation.mutate({ fixId: fix.id, editedContent: edited });
    } else {
      approveMutation.mutate({ fixId: fix.id });
    }
  };

  return (
    <div className="rounded-lg border border-teal/30 bg-teal-light/20 px-3 py-2 mt-1.5 space-y-2">
      <div className="flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-teal-dark" />
        <span className="text-xs font-medium text-teal-dark">AI Fix Suggestion</span>
        <Badge className="text-[10px] h-4 px-1.5 bg-teal-light text-teal-dark">
          {fix.status}
        </Badge>
        {isModified && (
          <span className="text-[10px] text-coral-dark italic">edited</span>
        )}
      </div>

      {isProcessing && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Processing...
        </div>
      )}

      {fix.proposed_content && (
        <div className="text-sm bg-background/80 rounded px-2 py-1.5 border border-border/40">
          {isEditing && isPending ? (
            <Textarea
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              className="text-xs min-h-[80px] resize-y border-0 p-0 shadow-none focus-visible:ring-0"
              autoFocus
              onBlur={() => setIsEditing(false)}
            />
          ) : (
            <p
              className={`whitespace-pre-wrap break-words text-xs ${isPending ? "cursor-text hover:bg-teal-light/30 rounded transition-colors" : ""}`}
              onClick={() => isPending && setIsEditing(true)}
              title={isPending ? "Click to edit suggestion" : undefined}
            >
              {editedText || originalText}
            </p>
          )}
        </div>
      )}

      {isPending && (
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            className="h-6 text-[11px] px-2"
            onClick={handleApprove}
            disabled={approveMutation.isPending}
          >
            <CheckCircle className="h-3 w-3 mr-1" />
            {isModified ? "Approve Edit" : "Approve"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-[11px] px-2"
            onClick={() => rejectMutation.mutate(fix.id)}
            disabled={rejectMutation.isPending}
          >
            <X className="h-3 w-3 mr-1" />
            Reject
          </Button>
          {!isEditing && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[11px] px-2 text-muted-foreground"
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="h-3 w-3 mr-1" />
              Edit
            </Button>
          )}
          {isModified && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[11px] px-1.5 text-muted-foreground"
              onClick={() => { setEditedText(originalText); setIsEditing(false); }}
            >
              <Eraser className="h-3 w-3 mr-1" />
              Reset
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor Comment Card (with Reply, AI Fix, Teach buttons)
// ---------------------------------------------------------------------------
function EditorCommentCard({
  comment,
  contentId,
  tenantId,
  fixes,
}: {
  comment: CommentWithReplies;
  contentId: string;
  tenantId: string;
  fixes: AIFix[];
}) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [taught, setTaught] = useState(false);

  const { createComment } = useContentComments(null, tenantId, { enabled: false });
  const triggerAIFix = useTriggerAIFix(contentId);
  const teachComment = useTeachComment(contentId || null);

  const commentFix = fixes.find(
    (f) => f.source_comment_id === comment.id && (f.status === "pending" || f.status === "processing"),
  );
  const isAIFixing = comment.status === "ai_fixing" || !!commentFix;
  const isResolved = comment.status === "resolved" || comment.status === "ai_fixed";

  const handleReply = async () => {
    if (!replyText.trim()) return;
    await createComment({
      comment_text: replyText.trim(),
      comment_type: "general",
      parent_comment_id: comment.id,
      block_id: comment.block_id,
    });
    setReplyText("");
    setShowReply(false);
  };

  const handleAIFix = () => {
    triggerAIFix.mutate({
      comment_id: comment.id,
      fix_type: "text_rewrite",
      block_id: comment.block_id ?? undefined,
      mode: "suggest",
    });
  };

  const handleTeach = () => {
    teachComment.mutate(
      { commentId: comment.id },
      { onSuccess: () => setTaught(true) },
    );
  };

  return (
    <div className="rounded-lg bg-teal-light/40 border border-teal/15 px-3 py-2 mb-1.5 group/ecard">
      {/* Header row */}
      <div className="flex items-center gap-1.5 mb-0.5">
        <span className="text-xs font-medium text-teal-dark">
          {comment.author_name}
        </span>
        <span className="text-[11px] text-teal-dark/50">
          {formatRelativeTime(comment.created_at)}
        </span>
        {comment.comment_type !== "general" && (
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded-full ${
              COMMENT_TYPE_CONFIG[comment.comment_type]?.color ||
              "bg-muted text-foreground"
            }`}
          >
            {COMMENT_TYPE_CONFIG[comment.comment_type]?.label ||
              comment.comment_type}
          </span>
        )}
        {isResolved && (
          <Badge className="bg-teal-light text-teal-dark text-[10px] h-4 px-1.5">
            {comment.status === "ai_fixed" ? "AI Fixed" : "Resolved"}
          </Badge>
        )}
        {isAIFixing && (
          <Badge className="bg-coral-light text-coral text-[10px] h-4 px-1.5">
            <Loader2 className="h-2.5 w-2.5 animate-spin mr-0.5" />
            AI Fixing
          </Badge>
        )}
      </div>

      {/* Comment text */}
      <p className="text-sm whitespace-pre-wrap break-words">
        {comment.comment_text}
      </p>

      {/* Action buttons */}
      {!isResolved && (
        <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover/ecard:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-[11px] px-1.5 text-teal-dark hover:text-teal-dark"
            onClick={() => setShowReply(!showReply)}
          >
            <Reply className="h-3 w-3 mr-1" />
            Reply
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-[11px] px-1.5 text-teal-dark hover:text-teal-dark"
            onClick={handleAIFix}
            disabled={triggerAIFix.isPending || isAIFixing}
          >
            <Sparkles className="h-3 w-3 mr-1" />
            {triggerAIFix.isPending ? "..." : "Ask AI"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-[11px] px-1.5 text-teal-dark hover:text-teal-dark"
            onClick={handleTeach}
            disabled={teachComment.isPending || taught}
          >
            <GraduationCap className="h-3 w-3 mr-1" />
            {taught ? "Taught" : teachComment.isPending ? "..." : "Teach AI"}
          </Button>
        </div>
      )}

      {/* AI Fix preview */}
      {commentFix && (
        <AIFixPreviewInline fix={commentFix} contentId={contentId} />
      )}

      {/* Reply form */}
      {showReply && (
        <div className="mt-2 space-y-1.5">
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a reply..."
            className="min-h-[48px] text-xs resize-y"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleReply();
              }
            }}
          />
          <div className="flex gap-1 justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[11px] px-2"
              onClick={() => { setShowReply(false); setReplyText(""); }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-6 text-[11px] px-2"
              onClick={handleReply}
              disabled={!replyText.trim()}
            >
              <Send className="h-3 w-3 mr-1" />
              Reply
            </Button>
          </div>
        </div>
      )}

      {/* Threaded replies */}
      {comment.replies.length > 0 && (
        <div className="mt-1.5 pl-3 border-l-2 border-teal/20 space-y-1">
          {comment.replies.map((reply) => (
            <div key={reply.id} className="text-sm">
              <span className="text-xs font-medium text-teal-dark">
                {reply.author_name}
              </span>{" "}
              <span className="text-[11px] text-teal-dark/50">
                {formatRelativeTime(reply.created_at)}
              </span>
              <p className="whitespace-pre-wrap break-words">
                {reply.comment_text}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// QA Comment item (editable)
// ---------------------------------------------------------------------------
const BLOCK_TYPE_LABELS: Record<string, string> = {
  p: "Paragraph",
  h2: "Heading 2",
  h3: "Heading 3",
  h4: "Heading 4",
  ul: "Bullet List",
  ol: "Numbered List",
  table: "Table",
  blockquote: "Blockquote",
  img: "Image",
  faq: "FAQ",
  cta: "CTA",
  intro: "Intro",
  conclusion: "Conclusion",
};

function formatBlockLabel(blockId: string | null | undefined): string | null {
  if (!blockId) return null;
  if (blockId === "_general" || blockId === "_unanchored") return "General";
  // e.g. "p-block-008" → "Paragraph #8", "h2-block-003" → "Heading 2 #3"
  const parts = blockId.split("-");
  const typeKey = parts[0]?.toLowerCase();
  const friendlyType = BLOCK_TYPE_LABELS[typeKey] || typeKey?.toUpperCase() || blockId;
  if (parts.length >= 3) {
    const num = parseInt(parts[parts.length - 1], 10);
    return isNaN(num) ? friendlyType : `${friendlyType} #${num}`;
  }
  return friendlyType;
}

function CommentItem({
  comment,
  tenantId,
  contentId,
}: {
  comment: QAComment;
  tenantId: string;
  contentId: string;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.comment_text);
  const updateComment = useUpdateComment();
  const deleteComment = useDeleteComment();

  const isEdited = comment.updated_at !== comment.created_at;
  const blockLabel = formatBlockLabel(comment.block_id);

  const handleSave = () => {
    if (!editText.trim()) return;
    updateComment.mutate(
      {
        commentId: comment.id,
        tenantId,
        contentId,
        data: { comment_text: editText.trim() },
      },
      { onSuccess: () => setIsEditing(false) }
    );
  };

  return (
    <div className="rounded-lg bg-warm-bg border border-border/60 px-3 py-2.5 transition-colors hover:border-coral/30">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <div className="h-5 w-5 rounded-full bg-coral/10 flex items-center justify-center">
            <User className="h-3 w-3 text-coral" />
          </div>
          <span className="text-xs font-medium">{comment.author}</span>
          <span className="text-[11px] text-muted-foreground">
            {formatRelativeTime(comment.created_at)}
          </span>
          {blockLabel && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-coral-light text-coral-dark font-medium" title={`Block: ${comment.block_id}`}>
              {blockLabel}
            </span>
          )}
          {isEdited && (
            <span className="text-[10px] text-muted-foreground italic">(edited)</span>
          )}
        </div>
        {!isEditing && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover/comment:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              aria-label="Edit comment"
              onClick={() => {
                setEditText(comment.comment_text);
                setIsEditing(true);
              }}
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-destructive hover:text-destructive"
              aria-label="Delete comment"
              onClick={() =>
                deleteComment.mutate({ commentId: comment.id, tenantId, contentId })
              }
              disabled={deleteComment.isPending}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-2 mt-1">
          <Textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="min-h-[60px] resize-y text-sm"
            autoFocus
          />
          <div className="flex gap-1.5 justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => {
                setEditText(comment.comment_text);
                setIsEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleSave}
              disabled={!editText.trim() || updateComment.isPending}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm whitespace-pre-wrap break-words ml-7">
          {comment.comment_text}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Drawer
// ---------------------------------------------------------------------------
export function QANotesDrawer({ review, open, onClose, tenantId }: QANotesDrawerProps) {
  const [width, setWidth] = useState(getStoredWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [notes, setNotes] = useState("");
  const [newComment, setNewComment] = useState("");
  const [issuesOpen, setIssuesOpen] = useState(true);
  const [editorOpen, setEditorOpen] = useState(true);
  const [commentsOpen, setCommentsOpen] = useState(true);
  const [summaryText, setSummaryText] = useState<string | null>(null);
  const [commentBlockId, setCommentBlockId] = useState<string>("_general");

  const updateReview = useUpdateQAReview();
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const contentId = review?.content_id ?? "";
  const { data: commentsData, isLoading, error } = useQAComments(contentId, tenantId);
  const createComment = useCreateComment();

  const {
    comments: editorComments,
    isLoading: editorCommentsLoading,
  } = useContentComments(contentId || null, tenantId, { enabled: !!contentId });

  // AI fixes for this content
  const { data: aiFixes } = useListAIFixes(contentId || null, { enabled: !!contentId });

  // Summarizer
  const summarizeMutation = useSummarizeComments(contentId || null);

  const comments = commentsData?.comments ?? [];

  // Sync notes
  useEffect(() => {
    setNotes(review?.reviewer_notes ?? "");
    setSummaryText(null);
  }, [review?.content_id, review?.reviewer_notes]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments.length]);

  // Resize logic
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - e.clientX));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      localStorage.setItem(STORAGE_KEY, String(width));
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, width]);

  useEffect(() => {
    if (isDragging) {
      localStorage.setItem(STORAGE_KEY, String(width));
    }
  }, [width, isDragging]);

  const saveNotes = useCallback(
    (value: string) => {
      if (!review) return;
      updateReview.mutate({
        contentId: review.content_id,
        tenantId,
        data: { reviewer_notes: value || null },
      });
    },
    [review, updateReview, tenantId]
  );

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNotes(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => saveNotes(value), 2000);
  };

  const handleNotesBlur = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (review && notes !== (review.reviewer_notes ?? "")) {
      saveNotes(notes);
    }
  };

  const handleAddComment = () => {
    if (!newComment.trim() || !review) return;
    createComment.mutate(
      {
        contentId: review.content_id,
        tenantId,
        data: {
          comment_text: newComment.trim(),
          author: "admin",
          block_id: commentBlockId === "_general" ? null : commentBlockId,
        },
      },
      {
        onSuccess: () => {
          setNewComment("");
          setCommentBlockId("_general");
        },
      }
    );
  };

  const handleCommentKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleAddComment();
    }
  };

  const handleMarkReviewed = () => {
    if (!review) return;
    updateReview.mutate({
      contentId: review.content_id,
      tenantId,
      data: { reviewed_by: "admin" },
    });
  };

  const handleClearAllIssues = () => {
    if (!review) return;
    const clearData: Record<string, boolean> = {};
    for (const rule of QA_RULES) {
      clearData[rule.key] = false;
    }
    updateReview.mutate({
      contentId: review.content_id,
      tenantId,
      data: clearData as Record<QARuleKey, boolean>,
    });
  };

  const handleSummarize = () => {
    summarizeMutation.mutate(undefined, {
      onSuccess: (data) => setSummaryText(data.summary),
    });
  };

  const issueCount = review ? getIssueCount(review) : 0;
  const isReviewed = !!review?.reviewed_at;

  // Editor comment stats
  const editorEntries = Object.entries(editorComments);
  const editorTotal = editorEntries.reduce(
    (sum, [, group]) =>
      sum + group.length + group.reduce((rs, c) => rs + c.replies.length, 0),
    0,
  );

  const totalAllComments = editorTotal + comments.length;

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed top-0 right-0 z-50 h-full bg-background border-l shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
        style={{ width }}
      >
        {/* Resize handle */}
        <div
          className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize group/resize z-10 flex items-center"
          onMouseDown={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
        >
          <div className="absolute inset-y-0 left-0 w-1.5 bg-transparent group-hover/resize:bg-coral/20 transition-colors" />
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-8 flex items-center justify-center opacity-0 group-hover/resize:opacity-100 transition-opacity">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b bg-warm-bg/50">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold leading-snug truncate pr-2">
                {review?.title || "Untitled Post"}
              </h2>
              <div className="flex items-center gap-3 mt-1.5">
                {review?.keyword && (
                  <span className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{review.keyword}</span>
                  </span>
                )}
                {review?.total_word_count ? (
                  <span className="text-xs text-muted-foreground">
                    {review.total_word_count.toLocaleString()} words
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-2 mt-2">
                {review?.wordpress_post_url && (
                  <a
                    href={review.wordpress_post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-coral hover:text-coral-hover transition-colors"
                  >
                    <ExternalLink className="h-3 w-3" />
                    View on WordPress
                  </a>
                )}
                {isReviewed ? (
                  <Badge className="bg-teal-light text-teal-dark border-teal/20 text-[10px] h-5">
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    Reviewed
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-[10px] h-5">
                    Pending Review
                  </Badge>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 -mr-2 -mt-1"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Scrollable content */}
        <ScrollArea className="flex-1">
          <div className="px-5 py-4 space-y-1">

            {/* AI Summary */}
            {totalAllComments > 0 && (
              <div className="pb-2">
                {!summaryText ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full h-8 text-xs border-dashed"
                    onClick={handleSummarize}
                    disabled={summarizeMutation.isPending}
                  >
                    {summarizeMutation.isPending ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        Summarizing {totalAllComments} comments...
                      </>
                    ) : (
                      <>
                        <BrainCircuit className="h-3.5 w-3.5 mr-1.5" />
                        AI Summarize All Comments ({totalAllComments})
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="rounded-lg border border-teal/30 bg-gradient-to-br from-teal-light/30 to-warm-bg px-3.5 py-2.5">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <BrainCircuit className="h-3.5 w-3.5 text-teal-dark" />
                        <span className="text-xs font-semibold text-teal-dark">AI Summary</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 text-[10px] px-1.5"
                        onClick={() => setSummaryText(null)}
                      >
                        Dismiss
                      </Button>
                    </div>
                    <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                      {summaryText}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Issues section */}
            <Collapsible open={issuesOpen} onOpenChange={setIssuesOpen}>
              <CollapsibleTrigger asChild>
                <div>
                  <SectionHeader
                    icon={issueCount > 0 ? AlertTriangle : ShieldCheck}
                    label="Issues"
                    count={issueCount}
                    open={issuesOpen}
                    onToggle={() => setIssuesOpen(!issuesOpen)}
                    variant={issueCount > 0 ? "coral" : "default"}
                  />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="pb-3 pt-1">
                  {issueCount === 0 ? (
                    <p className="text-sm text-muted-foreground pl-6">No issues flagged</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 pl-6">
                      {QA_RULES.map((rule) => {
                        const hasIssue = review?.[rule.key as QARuleKey];
                        if (!hasIssue) return null;
                        return (
                          <Badge
                            key={rule.key}
                            variant="destructive"
                            className="text-xs cursor-default"
                            title={rule.description}
                          >
                            {rule.shortLabel}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  {isReviewed && (
                    <div className="mt-2 pl-6 space-y-0.5 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <User className="h-3 w-3" />
                        <span>by {review?.reviewed_by || "Unknown"}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3 w-3" />
                        <span>
                          {new Date(review!.reviewed_at!).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>

            <div className="border-t border-border/40" />

            {/* Quick Notes */}
            <div className="py-3">
              <div className="flex items-center gap-2 mb-2">
                <StickyNote className="h-4 w-4 text-foreground" />
                <span className="text-sm font-semibold">Quick Notes</span>
              </div>
              <Textarea
                value={notes}
                onChange={handleNotesChange}
                onBlur={handleNotesBlur}
                placeholder="Add quick notes about this post..."
                className="min-h-[72px] resize-y text-sm"
              />
              <p className="text-[11px] text-muted-foreground mt-1 pl-0.5">
                Auto-saves after 2s or on blur
              </p>
            </div>

            <div className="border-t border-border/40" />

            {/* Editor Comments */}
            {(editorCommentsLoading || editorTotal > 0) && (
              <>
                <Collapsible open={editorOpen} onOpenChange={setEditorOpen}>
                  <CollapsibleTrigger asChild>
                    <div className="pt-1">
                      <SectionHeader
                        icon={FileEdit}
                        label="Editor Comments"
                        count={editorTotal}
                        open={editorOpen}
                        onToggle={() => setEditorOpen(!editorOpen)}
                        variant="teal"
                      />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="pb-3 pt-1">
                      {editorCommentsLoading ? (
                        <p className="text-sm text-muted-foreground pl-6 py-1">Loading...</p>
                      ) : (
                        <div className="space-y-2 pl-1">
                          {editorEntries.map(([blockId, blockComments]) => (
                            <div key={blockId}>
                              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 pl-1">
                                {blockId === "_unanchored"
                                  ? "General"
                                  : formatBlockLabel(blockId) || blockId}
                              </div>
                              {blockComments.map((comment) => (
                                <EditorCommentCard
                                  key={comment.id}
                                  comment={comment}
                                  contentId={contentId}
                                  tenantId={tenantId}
                                  fixes={aiFixes ?? []}
                                />
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <div className="border-t border-border/40" />
              </>
            )}

            {/* QA Comments */}
            <Collapsible open={commentsOpen} onOpenChange={setCommentsOpen}>
              <CollapsibleTrigger asChild>
                <div className="pt-1">
                  <SectionHeader
                    icon={MessageSquare}
                    label="Comments"
                    count={comments.length}
                    open={commentsOpen}
                    onToggle={() => setCommentsOpen(!commentsOpen)}
                    variant="coral"
                  />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="pb-3 pt-1">
                  {isLoading ? (
                    <p className="text-sm text-muted-foreground pl-6 py-1">Loading...</p>
                  ) : error ? (
                    <p className="text-sm text-destructive pl-6 py-1">Failed to load comments</p>
                  ) : comments.length === 0 ? (
                    <p className="text-sm text-muted-foreground pl-6 py-1">
                      No comments yet. Start the conversation below.
                    </p>
                  ) : (
                    <ScrollArea className="max-h-[300px]" ref={scrollRef}>
                      <div className="space-y-2 pr-2">
                        {(() => {
                          // Group QA comments by block_id
                          const grouped = new Map<string, QAComment[]>();
                          for (const c of comments) {
                            const key = c.block_id || "_general";
                            const arr = grouped.get(key) || [];
                            arr.push(c);
                            grouped.set(key, arr);
                          }
                          // Sort: _general first, then alphabetically
                          const sortedKeys = [...grouped.keys()].sort((a, b) => {
                            if (a === "_general") return -1;
                            if (b === "_general") return 1;
                            return a.localeCompare(b);
                          });
                          return sortedKeys.map((blockKey) => (
                            <div key={blockKey}>
                              {grouped.size > 1 && (
                                <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 pl-1">
                                  {formatBlockLabel(blockKey) || "General"}
                                </div>
                              )}
                              {grouped.get(blockKey)!.map((comment) => (
                                <div key={comment.id} className="group/comment mb-1.5">
                                  <CommentItem
                                    comment={comment}
                                    tenantId={tenantId}
                                    contentId={contentId}
                                  />
                                </div>
                              ))}
                            </div>
                          ));
                        })()}
                      </div>
                    </ScrollArea>
                  )}

                  {/* Add comment with block picker */}
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <select
                        value={commentBlockId}
                        onChange={(e) => setCommentBlockId(e.target.value)}
                        className="text-[11px] h-7 px-2 rounded-md border border-border bg-background text-foreground"
                        title="Attach comment to a content block"
                      >
                        <option value="_general">General</option>
                        {/* Populate from editor comment blocks as available block references */}
                        {editorEntries.map(([blockId]) => (
                          <option key={blockId} value={blockId}>
                            {formatBlockLabel(blockId) || blockId}
                          </option>
                        ))}
                      </select>
                      <span className="text-[10px] text-muted-foreground">block</span>
                    </div>
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyDown={handleCommentKeyDown}
                      placeholder="Add a comment..."
                      className="min-h-[56px] resize-y text-sm"
                    />
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-muted-foreground">
                        Ctrl+Enter to send
                      </p>
                      <Button
                        size="sm"
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || createComment.isPending}
                        className="h-7 text-xs"
                      >
                        <Send className="h-3 w-3 mr-1.5" />
                        Send
                      </Button>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </ScrollArea>

        {/* Sticky footer */}
        <div className="border-t bg-warm-bg/50 px-5 py-3 space-y-2">
          <Button
            onClick={handleMarkReviewed}
            disabled={isReviewed || updateReview.isPending}
            className="w-full h-9"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {isReviewed ? "Already Reviewed" : "Mark as Reviewed"}
          </Button>
          {issueCount > 0 && (
            <Button
              variant="outline"
              onClick={handleClearAllIssues}
              disabled={updateReview.isPending}
              className="w-full h-9"
            >
              <Eraser className="h-4 w-4 mr-2" />
              Clear All Issues ({issueCount})
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
